"use client";

import { useEffect, useState } from "react";
import { useTranslations } from "next-intl";

export type TocItem = {
  id: string;
  label: string;
};

type DocsTocProps = {
  items: TocItem[];
};

export function DocsToc({ items }: DocsTocProps) {
  const t = useTranslations("docs.nav");
  const [activeId, setActiveId] = useState<string | undefined>(items[0]?.id);

  useEffect(() => {
    if (typeof IntersectionObserver === "undefined") return;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setActiveId(entry.target.id);
          }
        }
      },
      { rootMargin: "-20% 0% -70% 0%", threshold: 0 },
    );
    for (const item of items) {
      const el = document.getElementById(item.id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [items]);

  if (items.length === 0) return null;

  return (
    <aside className="not-prose mb-8 rounded-xl border bg-muted/30 px-5 py-4">
      <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {t("onThisPage")}
      </p>
      <ul className="space-y-2 text-sm">
        {items.map((item) => (
          <li key={item.id}>
            <a
              href={`#${item.id}`}
              className={
                item.id === activeId
                  ? "text-primary font-medium"
                  : "text-muted-foreground hover:text-foreground transition-colors"
              }
            >
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </aside>
  );
}
